import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  User, Shield, Briefcase, Wrench, MessageSquare, 
  Mail, Lock, CheckCircle2, AlertCircle, Trash2, Edit3, 
  Plus, Search, LayoutGrid, List, Smartphone, Copy, Check, ChevronRight, X,
  RefreshCcw, Eye, EyeOff, Save, Info, GripVertical, Award, Camera
} from 'lucide-react';
import { pb, getFileUrl } from '../../lib/pb';
import '../../styles/admin.css';

// --- ICONS MAPPING ---
const ROLE_CONFIG = {
  'admin': { icon: <Shield size={16} />, label: 'Administrateur', color: '#6366f1', bg: '#eef2ff' },
  'commercial': { icon: <Briefcase size={16} />, label: 'Commercial', color: '#f59e0b', bg: '#fffbeb' },
  'worker': { icon: <Wrench size={16} />, label: 'Technicien / Chantier', color: '#10b981', bg: '#f0fdf4' }
};

export default function AdminTeam() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [draggingId, setDraggingId] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await pb.collection('team_members').getFullList({
        filter: search ? `name ~ "${search}" || role ~ "${search}"` : '',
        expand: 'user',
        sort: 'order'
      });
      setItems(res);
    } catch (e) {
      setError("Erreur de synchronisation de l'équipe");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search]);

  useEffect(() => {
    let unsub = null;
    (async () => {
      try {
        unsub = await pb.collection('team_members').subscribe('*', () => load());
      } catch (e) { console.error(e); }
    })();
    return () => { if (unsub) pb.collection('team_members').unsubscribe('*'); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTogglePublished = async (m) => {
    const oldStatus = m.published;
    setItems(prev => prev.map(item => item.id === m.id ? { ...item, published: !oldStatus } : item));

    try {
      await pb.collection('team_members').update(m.id, { published: !oldStatus });
      setSuccess(!oldStatus ? 'Membre publié sur le site ⭐' : 'Membre masqué du site 🌑');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      setItems(prev => prev.map(item => item.id === m.id ? { ...item, published: oldStatus } : item));
      setError("Erreur lors de la mise à jour");
    }
  };

  const openCreate = () => setEditing({ name: '', role: '', bio: '', published: true });
  const openEdit = (member) => setEditing(member);

  const handleDragStart = (id) => setDraggingId(id);

  const handleDragOver = (targetId, e) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;
    setItems(prev => {
      const next = [...prev];
      const fromIdx = next.findIndex(s => s.id === draggingId);
      const toIdx = next.findIndex(s => s.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const persistOrder = async () => {
    if (!items.length) return;
    try {
      const updates = items.map((s, index) => {
        const newOrder = (index + 1) * 10;
        if (s.order !== newOrder) return pb.collection('team_members').update(s.id, { order: newOrder });
        return null;
      }).filter(Boolean);
      if (updates.length) {
        await Promise.all(updates);
        setSuccess('Hiérarchie mise à jour ✨');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError("Erreur de réorganisation"); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    persistOrder();
    setDraggingId(null);
  };

  return (
    <div className="admin-main animation-fade-in">
      <header className="admin-header-premium">
        <div className="header-content">
          <div className="header-tag">Ressources Humaines</div>
          <h1>L'Équipe d'Excellence <span className="count-badge">{items.length}</span></h1>
          <p className="header-subtitle">Gérez vos colllaborateurs, leurs accès au planning mobile et leur visibilité publique.</p>
        </div>
        <button className="btn-add-premium" onClick={openCreate}>
          <Plus size={20} />
          <span>Nouvelle Recrue</span>
        </button>
      </header>

      {success && <div className="toast-success"><CheckCircle2 size={18}/> {success}</div>}
      {error && <div className="toast-error"><AlertCircle size={18}/> {error}</div>}

      <div className="admin-controls-bar">
        <div className="search-box-premium">
          <Search size={18} className="search-icon" />
          <input 
            placeholder="Rechercher par nom, métier ou rôle..." 
            value={search} 
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="filters-and-view">
          <div className="view-switcher-premium">
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <RefreshCcw className="spin" size={32} />
          <p>Synchronisation des profils...</p>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="empty-state-card">
          <User size={48} />
          <h3>Seul au monde ?</h3>
          <p>Ajoutez les membres de votre équipe pour commencer à leur assigner des chantiers.</p>
          <button className="btn filled" onClick={openCreate}>Ajouter un membre</button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className={`team-content-container ${viewMode}`}>
          {viewMode === 'list' ? (
            <div className="premium-list">
              {items.map((m) => (
                <div 
                  key={m.id} 
                  className={`list-item-row ${draggingId === m.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(m.id)}
                  onDragOver={(e) => handleDragOver(m.id, e)}
                  onDrop={handleDrop}
                >
                  <div className="drag-handle"><GripVertical size={18}/></div>
                  <div className="item-icon-box rounded">
                    {m.photo ? (
                      <img src={getFileUrl(m, m.photo)} alt={m.name} />
                    ) : (
                      <User size={20} className="placeholder" />
                    )}
                  </div>
                  <div className="item-info">
                    <div className="item-title">{m.name}</div>
                    <div className="item-meta">
                      {m.role || 'Personnel AEF'} • {m.expand?.user?.email || 'Pas d\'email'}
                    </div>
                  </div>
                  <div className="item-status">
                     <button className={`status-pill ${m.published ? 'online' : 'offline'}`} onClick={() => handleTogglePublished(m)}>
                        {m.published ? <Eye size={14}/> : <EyeOff size={14}/>}
                        <span>{m.published ? 'Public' : 'Masqué'}</span>
                     </button>
                  </div>
                  <div className="item-actions">
                    <button className="action-btn edit" onClick={() => openEdit(m)} title="Modifier">
                      <Edit3 size={18}/>
                    </button>
                    <button className="action-btn delete" onClick={() => { if(window.confirm('Supprimer ce membre ?')) { pb.collection('team_members').delete(m.id).then(() => load()); } }} title="Supprimer">
                      <Trash2 size={18}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="premium-grid-team">
              {items.map((m) => {
                const conf = ROLE_CONFIG[m.expand?.user?.role] || ROLE_CONFIG.worker;
                return (
                  <div 
                    key={m.id} 
                    className="member-card-premium animation-slide-up"
                    onClick={() => openEdit(m)}
                    draggable
                    onDragStart={() => handleDragStart(m.id)}
                    onDragOver={(e) => handleDragOver(m.id, e)}
                    onDrop={handleDrop}
                  >
                    <div className="member-avatar-box">
                      <div className="avatar-main" style={{ borderColor: conf.color }}>
                         {m.photo ? <img src={getFileUrl(m, m.photo)} alt={m.name} /> : <User size={30} color="#94a3b8" />}
                      </div>
                      <div className="role-tag-icon" style={{ background: conf.color }}>
                         {conf.icon}
                      </div>
                    </div>
                    
                    <div className="member-body">
                      <h3 className="member-name">{m.name}</h3>
                      <p className="member-position">{m.role || 'Collaborateur'}</p>
                      
                      <div className="member-badges">
                         {m.expand?.user ? (
                            <span className="badge-app active"><Smartphone size={12}/> App Connectée</span>
                         ) : (
                            <span className="badge-app none">Accès restreint</span>
                         )}
                         {m.published && <span className="badge-site"><Eye size={12}/> Public</span>}
                      </div>

                      <div className="card-actions-row">
                         <button className={`btn-mini ${m.published ? 'online' : 'offline'}`} onClick={(e) => { e.stopPropagation(); handleTogglePublished(m); }} title={m.published ? 'Masquer' : 'Publier'}>
                            {m.published ? <Eye size={16}/> : <EyeOff size={16}/>}
                         </button>
                         <button className="btn-mini edit" onClick={(e) => { e.stopPropagation(); openEdit(m); }} title="Modifier"><Edit3 size={16}/></button>
                         <button className="btn-mini delete" onClick={(e) => { e.stopPropagation(); if(window.confirm('Supprimer ce membre ?')) { pb.collection('team_members').delete(m.id).then(() => load()); } }} title="Supprimer"><Trash2 size={16}/></button>
                      </div>

                      <div className="member-footer-hint">
                         <Mail size={12}/> {m.expand?.user?.email || 'Non renseigné'}
                         <ChevronRight size={14} className="arrow-hint"/>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {editing && (
        <TeamStudio
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <style>{`
        .premium-grid-team { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 25px; padding: 20px 0; }
        
        .member-card-premium { background: #fff; border-radius: 32px; border: 1px solid var(--admin-border); padding: 30px 20px; display: flex; flex-direction: column; align-items: center; text-align: center; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); cursor: grab; position: relative; }
        .member-card-premium:hover { transform: translateY(-8px); border-color: var(--admin-primary); box-shadow: 0 20px 40px -15px rgba(0,0,0,0.08); }
        
        .member-avatar-box { position: relative; margin-bottom: 20px; }
        .avatar-main { width: 100px; height: 100px; border-radius: 50%; border: 3px solid #f1f5f9; padding: 4px; background: #fff; overflow: hidden; display: flex; align-items: center; justify-content: center; }
        .avatar-main img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
        
        .role-tag-icon { position: absolute; bottom: 0; right: 0; width: 32px; height: 32px; border-radius: 50%; color: #fff; display: flex; align-items: center; justify-content: center; border: 3px solid #fff; box-shadow: 0 4px 10px rgba(0,0,0,0.15); }
        
        .member-name { font-size: 19px; font-weight: 900; color: #0f172a; margin: 0 0 5px 0; }
        .member-position { font-size: 14px; font-weight: 700; color: #94a3b8; margin: 0 0 15px 0; }
        
        .member-badges { display: flex; gap: 8px; justify-content: center; margin-bottom: 20px; }
        .badge-app { font-size: 10px; font-weight: 800; text-transform: uppercase; padding: 4px 10px; border-radius: 8px; display: flex; align-items: center; gap: 5px; }
        .badge-app.active { background: #ecfdf5; color: #10b981; }
        .badge-app.none { background: #f1f5f9; color: #94a3b8; }
        .badge-site { background: #fffbeb; color: #d97706; font-size: 10px; font-weight: 800; text-transform: uppercase; padding: 4px 10px; border-radius: 8px; display: flex; align-items: center; gap: 5px; }

        .member-footer-hint { margin-top: 10px; padding-top: 15px; border-top: 1px solid #f8fafc; font-size: 11px; color: #cbd5e1; display: flex; align-items: center; gap: 8px; width: 100%; justify-content: center; position: relative; }
        .arrow-hint { position: absolute; right: 0; opacity: 0; transform: translateX(-10px); transition: all 0.3s; }
        .member-card-premium:hover .arrow-hint { opacity: 1; transform: translateX(0); color: var(--admin-primary); }

        .item-icon-box.rounded { border-radius: 50%; width: 44px; height: 44px; }
        .item-icon-box.rounded img { border-radius: 50%; }

        @media (max-width: 600px) {
          .premium-grid-team { grid-template-columns: 1fr; }
          .member-card-premium { flex-direction: row; text-align: left; padding: 20px; }
          .member-avatar-box { margin-bottom: 0; margin-right: 20px; }
          .avatar-main { width: 70px; height: 70px; }
          .member-body { flex: 1; }
          .member-badges { justify-content: flex-start; }
          .member-footer-hint { width: auto; border: none; padding: 0; margin-top: 5px; justify-content: flex-start; }
        }
      `}</style>
    </div>
  );
}

function TeamStudio({ member, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [name, setName] = useState(member.name || '');
  const [role, setRole] = useState(member.role || '');
  const [bio, setBio] = useState(member.bio || '');
  const [published, setPublished] = useState(member.published ?? true);
  const [photo, setPhoto] = useState(member.photo ? { type: 'existing', name: member.photo } : null);

  const linkedUser = member.expand?.user || {};
  const [access, setAccess] = useState({
    active: !!member.user,
    email: linkedUser.email || '',
    password: '',
    role: linkedUser.role || 'worker'
  });

  const fileInputRef = useRef(null);

  const compressImage = (file, maxWidth = 600, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
          canvas.width = width; canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Compression error')), 'image/jpeg', quality);
        } catch (err) { reject(err); }
      };
      img.onerror = () => reject(new Error("Image error"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const finalFile = new File([compressed], file.name, { type: 'image/jpeg' });
      setPhoto({ type: 'new', file: finalFile });
    } catch (err) { setError("Erreur photo"); }
  };

  const onSave = async (e) => {
    e.preventDefault(); setIsSaving(true); setError('');
    try {
      let userId = member.user;
      if (access.active) {
        const userData = { email: access.email, role: access.role, name: name, password: access.password || undefined, passwordConfirm: access.password || undefined, emailVisibility: true };
        if (userId) await pb.collection('users').update(userId, userData);
        else {
          if (!access.password) throw new Error("Mot de passe requis pour la création.");
          const newUser = await pb.collection('users').create({ ...userData, username: access.email.split('@')[0] + Math.floor(Math.random()*1000) });
          userId = newUser.id;
        }
      }

      const formData = new FormData();
      formData.append('name', name);
      formData.append('role', role);
      formData.append('bio', bio);
      formData.append('published', published);
      if (userId) formData.append('user', userId);
      
      if (photo && photo.type === 'new') {
        formData.append('photo', photo.file);
      } else if (!photo) {
        formData.append('photo', '');
      }

      if (member.id) await pb.collection('team_members').update(member.id, formData);
      else await pb.collection('team_members').create(formData);
      onSaved();
    } catch (err) { setError(err.message || 'Erreur lors de la sauvegarde'); } 
    finally { setIsSaving(false); }
  };

  const shareWhatsApp = () => {
     const text = `Salut ${name} !\n\nVoici tes accès pour le planning AEF 32 :\n🔗 Dashboard : ${window.location.origin}\n📧 Email : ${access.email}\n🔑 Mot de passe : ${access.password || 'Inchangé'}`;
     window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const previewUrl = photo ? (photo.type === 'existing' ? getFileUrl(member, photo.name) : URL.createObjectURL(photo.file)) : '';

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal-container studio-team animation-slide-up">
        <header className="modal-header-premium">
          <div className="header-info">
             <div className="header-tag">{member.id ? 'Fiche Salarié' : 'Nouvelle Recrue'}</div>
             <h2>Studio Collaborateur</h2>
          </div>
          <button className="btn-close-modal" onClick={onClose}><X size={20}/></button>
        </header>

        <div className="modal-tabs">
          <button className={activeTab === 'profile' ? 'active' : ''} onClick={() => setActiveTab('profile')}>
            <User size={16}/> <span>Compétences</span>
          </button>
          <button className={activeTab === 'access' ? 'active' : ''} onClick={() => setActiveTab('access')}>
            <Smartphone size={16}/> <span>Accès Mobile</span>
          </button>
        </div>

        <form onSubmit={onSave} className="modal-body-premium">
           {error && <div className="modal-error"><AlertCircle size={14}/> {error}</div>}

           <div className="tab-panels">
              {activeTab === 'profile' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="visual-studio" style={{ marginBottom: 25 }}>
                      <div className="preview-main-box" style={{ borderRadius: '50%' }}>
                         {previewUrl ? <img src={previewUrl} alt="Avatar" style={{ borderRadius: '50%' }} /> : <User size={48} color="#cbd5e1" />}
                      </div>
                      <div className="visual-actions">
                         <label className="btn-upload-premium">
                            <Camera size={18}/> <span>Changer la photo de profil</span>
                            <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
                         </label>
                         {photo && <button type="button" className="btn-remove-media" onClick={() => setPhoto(null)}><Trash2 size={16}/> Supprimer</button>}
                         <p className="field-hint">Photo carrée recommandée pour un rendu optimal.</p>
                      </div>
                   </div>

                   <div className="form-group-premium">
                      <label>Nom & Prénom</label>
                      <input value={name} onChange={e => setName(e.target.value)} required placeholder="ex: Julien Delacroix" />
                   </div>
                   <div className="form-group-premium">
                      <label>Intitulé du poste</label>
                      <input value={role} onChange={e => setRole(e.target.value)} placeholder="ex: Spécialiste Isolation RGE" />
                   </div>
                   <div className="form-group-premium">
                      <label>Note interne / Bio</label>
                      <textarea rows={3} value={bio} onChange={e => setBio(e.target.value)} placeholder="Quelques mots sur le parcours du collaborateur..." />
                   </div>
                   <div className="toggle-switch-group">
                      <span>Visible sur la page Équipe du site</span>
                      <label className="switch">
                         <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                         <span className="slider round"></span>
                      </label>
                   </div>
                </div>
              )}

              {activeTab === 'access' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="access-config-card">
                      <div className="toggle-switch-group" style={{ background: 'transparent', padding: 0, marginBottom: 25 }}>
                         <div>
                            <div style={{ fontWeight: 900, color: '#0f172a' }}>Autoriser la connexion aux outils</div>
                            <p className="field-hint">Permet de voir son planning sur smartphone.</p>
                         </div>
                         <label className="switch">
                            <input type="checkbox" checked={access.active} onChange={e => setAccess({...access, active: e.target.checked})} />
                            <span className="slider round"></span>
                         </label>
                      </div>

                      {access.active && (
                        <div className="access-form-grid animation-slide-up">
                           <div className="form-group-premium">
                              <label>Email professionnel</label>
                              <div style={{ position: 'relative' }}>
                                 <Mail size={16} style={{ position: 'absolute', left: 16, top: 16, color: '#94a3b8' }} />
                                 <input type="email" value={access.email} onChange={e => setAccess({...access, email: e.target.value})} style={{ paddingLeft: 45 }} placeholder="email@aef32.com" required />
                              </div>
                           </div>
                           <div className="form-group-premium">
                              <label>{member.id ? 'Définir un nouveau mot de passe' : 'Créer un mot de passe'}</label>
                              <div style={{ position: 'relative' }}>
                                 <Lock size={16} style={{ position: 'absolute', left: 16, top: 16, color: '#94a3b8' }} />
                                 <input type="password" value={access.password} onChange={e => setAccess({...access, password: e.target.value})} style={{ paddingLeft: 45 }} placeholder="Min 8 caractères..." />
                              </div>
                              <p className="field-hint">Laissez vide pour conserver l'ancien.</p>
                           </div>
                           <div className="form-group-premium">
                              <label>Type de privilèges</label>
                              <select value={access.role} onChange={e => setAccess({...access, role: e.target.value})}>
                                 <option value="worker">Exécution (Vue planning solo)</option>
                                 <option value="commercial">Suivi (Vue tous les chantiers)</option>
                                 <option value="admin">Maîtrise (Contrôle total admin)</option>
                              </select>
                           </div>
                           <button type="button" className="btn-whatsapp-premium" onClick={shareWhatsApp}>
                              <Smartphone size={20}/> <span>Envoyer les accès par WhatsApp</span>
                           </button>
                        </div>
                      )}
                   </div>
                </div>
              )}
           </div>

           <footer className="modal-footer-premium">
              <button type="button" className="btn-modal-cancel" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn-modal-save" disabled={isSaving}>
                 {isSaving ? <RefreshCcw size={18} className="spin"/> : <Save size={18}/>}
                 <span>{member.id ? 'Sauvegarder les modifications' : 'Créer le profil'}</span>
              </button>
           </footer>
        </form>
      </div>

      <style>{`
        .access-config-card { background: #f8fafc; padding: 25px; border-radius: 24px; border: 1px solid #f1f5f9; }
        .btn-whatsapp-premium { width: 100%; padding: 16px; border: none; background: #22c55e; color: #fff; border-radius: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; gap: 10px; cursor: pointer; transition: all 0.2s; margin-top: 10px; }
        .btn-whatsapp-premium:hover { background: #16a34a; transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(34, 197, 94, 0.3); }
        .modal-error { background: #fee2e2; color: #b91c1c; padding: 12px 18px; border-radius: 14px; margin-bottom: 20px; font-size: 13px; font-weight: 700; display: flex; align-items: center; gap: 10px; }
      `}</style>
    </div>
  );
}
